import styled, { css, keyframes } from 'styled-components';
import { FaSearch } from 'react-icons/fa';

/* ---- Page layout ---- */

export const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 80px);
  padding: 0;
  background-color: ${({ theme }) => theme.colors.background};
  overflow: hidden;
`;

export const PageHeader = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 24px;
  background-color: ${({ theme }) => theme.colors.background};
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrayOpacity};
  flex-shrink: 0;
`;

export const PageTitle = styled.h3`
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
  white-space: nowrap;
`;

export const SearchWrapper = styled.div`
  flex: 1;
  max-width: 400px;
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const SearchIcon = styled(FaSearch)`
  ${({ $spinning }) =>
    $spinning &&
    css`
      animation: ${spin} 0.7s linear infinite;
    `}
`;

/* ---- Filters bar (always visible) ---- */

export const FiltersBar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  padding: 10px 24px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightGrayOpacity};
  flex-shrink: 0;
  flex-wrap: wrap;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: ${({ $wide }) => ($wide ? '200px' : '130px')};
`;

export const FilterLabel = styled.label`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

export const DateTimeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const TimeInput = styled.input`
  height: 34px;
  width: 90px;
  padding: 0 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrayOpacity};
  background-color: ${({ theme }) => theme.colors.backgroundSecondary};
  color: ${({ theme }) => theme.colors.text};
  font-size: 12px;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.info};
  }
`;

/* ---- Status checkboxes ---- */

export const StatusGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding-top: 2px;
`;

const statusColorMap = {
  PENDENTE: '#D4764E',
  ANDAMENTO: '#5290A8',
  ENTREGA: '#5D9B76',
  CONCLUIDO: '#4caf50',
  CANCELADO: '#e53935',
};

export const StatusChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 28px;
  padding: 0 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.2px;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1.5px solid ${({ $status }) => statusColorMap[$status] || '#888'};
  background-color: ${({ $active, $status }) =>
    $active ? statusColorMap[$status] || '#888' : 'transparent'};
  color: ${({ $active, $status }) =>
    $active ? '#fff' : statusColorMap[$status] || '#888'};

  &:hover {
    opacity: 0.85;
  }
`;

const btnBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 36px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
`;

export const SearchBtn = styled.button`
  ${btnBase}
  gap: 8px;
  padding: 0 20px;
  height: 38px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, ${({ theme }) => theme.colors.info}, ${({ theme }) => theme.colors.info}dd);
  color: #fff;
  font-size: 13px;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 8px ${({ theme }) => theme.colors.info}40;
  align-self: flex-end;

  &:hover {
    box-shadow: 0 4px 14px ${({ theme }) => theme.colors.info}60;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 1px 4px ${({ theme }) => theme.colors.info}30;
  }
`;

/* ---- Table area ---- */

export const TableWrapper = styled.div`
  flex: 1;
  padding: 0 24px;
  overflow: hidden;
  margin-top: 8px;
`;

export const PaginationBar = styled.div`
  padding: 6px 24px;
  flex-shrink: 0;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrayOpacity};
`;

/* ---- Row action buttons ---- */

export const RowActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const RowBtn = styled.button`
  ${btnBase}
  height: 28px;
  padding: 0 10px;
  font-size: 11px;
  border: 1px solid ${({ theme }) => theme.colors.lightGrayOpacity};
  background-color: transparent;
  color: ${({ theme }) => theme.colors.textSecondary};

  &:hover {
    background-color: ${({ theme }) => theme.colors.info};
    color: #fff;
    border-color: ${({ theme }) => theme.colors.info};
  }

  svg {
    margin-right: 4px;
  }
`;

/* ---- Status badge (table) ---- */

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #fff;
  background-color: ${({ $status }) => statusColorMap[$status] || '#888'};
`;
